import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import case5Data from '../data/MSP_Case5_optimized.json';
import { CONVERSION_CONSTANTS } from '../utils/conversionUtils';

// Region mapping for cities
const cityToRegion = {
  'Berlin': 'Eastern Germany',
  'Bilbao': 'Northern Spain',
  'Marseille': 'Southern France',
  'Moselle': 'Eastern France',
  'Padua': 'North-East Italy',
  'Stockholm': 'Central Sweden',
  'Sundsval': 'Northern Sweden',
  'Shanghai': 'Eastern China',
  'BacNinh': 'Northern Vietnam',
  'Brussels': 'Central Belgium'
};

const Case5ScenarioComparison = () => {
  const [scenarios, setScenarios] = useState([
    { id: 1, name: 'Scenario 1', polysilicon: '', ingotWafer: '', cell: '', module: '', comboId: null },
  ]);
  const [unit, setUnit] = useState('m2'); // 'm2', 'w', or 'eurw'
  const [nextId, setNextId] = useState(2);

  // Color scheme
  const colors = {
    polysilicon: '#E78E42',
    ingotWafer: '#EAC144',
    cell: '#0A3C80',
    module: '#000755',
  };

  // Cost component colors
  const costColors = {
    'Materials': '#A0522D',
    'Material Transport': '#95A5A6',
    'Land': '#8B7355',
    'Equipment Depreciation': '#4A90E2',
    'Facility Depreciation': '#7B68EE',
    'Electricity': '#FFD93D',
    'Utilities': '#6BCB77',
    'Labour': '#E78E42',
    'SG&A': '#9370DB',
    'Income': '#27AE60',
  };

  const costLabels = {
    materials: 'Materials',
    material_transportation: 'Material Transport',
    land: 'Land',
    equipment_depreciation: 'Equipment Depreciation',
    facility_depreciation: 'Facility Depreciation',
    electricity: 'Electricity',
    utilities: 'Utilities',
    labour: 'Labour',
    sga: 'SG&A',
    income: 'Income',
  };

  // Extract available cities and create lookup map
  const { availableCities, lookupMap } = useMemo(() => {
    const cities = new Set();
    const map = {};

    Object.entries(case5Data).forEach(([id, data]) => {
      const locs = data.locations;
      cities.add(locs.polysilicon);
      cities.add(locs.ingot_wafer);
      cities.add(locs.cell);
      cities.add(locs.module);

      const key = `${locs.polysilicon}|${locs.ingot_wafer}|${locs.cell}|${locs.module}`;
      map[key] = id;
    });

    return {
      availableCities: Array.from(cities).sort(),
      lookupMap: map
    };
  }, []);

  // Convert polysilicon from USD/kg to USD/m²
  const convertPolysiliconToM2 = (usdPerKg) => {
    return usdPerKg * CONVERSION_CONSTANTS.polysiliconConsumption;
  };

  // Unit conversion functions
  const convertValue = (valueM2) => {
    if (unit === 'w') {
      return valueM2 / CONVERSION_CONSTANTS.powerPerM2;
    } else if (unit === 'eurw') {
      return (valueM2 / CONVERSION_CONSTANTS.powerPerM2) * CONVERSION_CONSTANTS.usdToEur;
    }
    return valueM2;
  };

  const getUnitLabel = () => {
    if (unit === 'w') return '$/W';
    if (unit === 'eurw') return '€/W';
    return '$/m²';
  };

  const addScenario = () => {
    if (scenarios.length < 6) {
      setScenarios([
        ...scenarios,
        { id: nextId, name: `Scenario ${nextId}`, polysilicon: '', ingotWafer: '', cell: '', module: '', comboId: null }
      ]);
      setNextId(nextId + 1);
    }
  };

  const removeScenario = (id) => {
    if (scenarios.length > 1) {
      setScenarios(scenarios.filter(s => s.id !== id));
    }
  };

  const updateScenario = (id, field, value) => {
    setScenarios(scenarios.map(scenario => {
      if (scenario.id === id) {
        const updated = { ...scenario, [field]: value };

        // Check if all fields are filled and get combo ID
        if (updated.polysilicon && updated.ingotWafer && updated.cell && updated.module) {
          const key = `${updated.polysilicon}|${updated.ingotWafer}|${updated.cell}|${updated.module}`;
          updated.comboId = lookupMap[key] || null;
        } else {
          updated.comboId = null;
        }

        return updated;
      }
      return scenario;
    }));
  };

  const updateScenarioName = (id, name) => {
    setScenarios(scenarios.map(scenario =>
      scenario.id === id ? { ...scenario, name } : scenario
    ));
  };

  // Prepare MSP comparison chart data (stage increments)
  const prepareMSPChartData = () => {
    return scenarios
      .filter(s => s.comboId && case5Data[s.comboId])
      .map(scenario => {
        const data = case5Data[scenario.comboId];
        const stats = data.statistics;

        // Convert polysilicon and calculate incremental contributions
        const polysiliconMSP = convertValue(convertPolysiliconToM2(stats.polysilicon_msp.mean));
        const ingotWaferMSP = convertValue(stats.ingot_wafer_msp.mean);
        const cellMSP = convertValue(stats.cell_msp.mean);
        const moduleMSP = convertValue(stats.module_msp.mean);

        // Cell packing factor
        const CELL_PACKING_FACTOR = 0.94;
        const cellContributionToModule = convertValue(stats.cell_msp.mean * CELL_PACKING_FACTOR);

        return {
          scenario: scenario.name,
          scenarioId: scenario.id,
          Polysilicon: polysiliconMSP,
          'Ingot/Wafer': ingotWaferMSP - polysiliconMSP,
          Cell: cellMSP - ingotWaferMSP,
          'Packing Adj': cellContributionToModule - cellMSP,
          Module: moduleMSP - cellContributionToModule,
          total: moduleMSP
        };
      });
  };

  // Prepare cost breakdown by stage chart data
  const prepareCostBreakdownData = (scenarioId) => {
    const scenario = scenarios.find(s => s.id === scenarioId);
    if (!scenario || !scenario.comboId) return [];

    const data = case5Data[scenario.comboId];
    if (!data || !data.cost_breakdown) return [];

    const stages = ['polysilicon', 'ingot_wafer', 'cell', 'module'];
    const orderedKeys = [
      'materials',
      'material_transportation',
      'land',
      'equipment_depreciation',
      'facility_depreciation',
      'electricity',
      'utilities',
      'labour',
      'sga',
      'income'
    ];

    return stages.map(stage => {
      const breakdown = data.cost_breakdown[stage];
      if (!breakdown) return { stage: stage };

      const dataPoint = {
        stage: stage === 'ingot_wafer' ? 'Ingot/Wafer' :
               stage.charAt(0).toUpperCase() + stage.slice(1)
      };

      orderedKeys.forEach(key => {
        if (breakdown[key] && breakdown[key].mean !== undefined && costLabels[key]) {
          const costValueM2 = stage === 'polysilicon'
            ? convertPolysiliconToM2(breakdown[key].mean)
            : breakdown[key].mean;
          dataPoint[costLabels[key]] = convertValue(costValueM2);
        }
      });

      return dataPoint;
    });
  };

  const mspChartData = prepareMSPChartData();

  const CustomMSPTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      const currency = unit === 'eurw' ? '€' : '$';
      const decimals = unit === 'm2' ? 2 : 3;

      return (
        <div className="bg-white p-3 border-2 rounded-lg shadow-lg" style={{ borderColor: '#E78E42' }}>
          <p className="font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {currency}{Math.abs(entry.value).toFixed(decimals)}
            </p>
          ))}
          <p className="font-semibold mt-2 pt-2 border-t">
            Total Module MSP: {currency}{total.toFixed(decimals)}
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomCostTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      const currency = unit === 'eurw' ? '€' : '$';
      const decimals = unit === 'm2' ? 2 : 3;

      return (
        <div className="bg-white p-3 border-2 rounded-lg shadow-lg" style={{ borderColor: '#E78E42' }}>
          <p className="font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {currency}{entry.value.toFixed(decimals)}
            </p>
          ))}
          <p className="font-semibold mt-2 pt-2 border-t">
            Total: {currency}{total.toFixed(decimals)}
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomMSPLegend = () => {
    const legendItems = [
      { name: 'Polysilicon', color: colors.polysilicon },
      { name: 'Ingot/Wafer', color: colors.ingotWafer },
      { name: 'Cell', color: colors.cell },
      { name: 'Packing Adj (-6%)', color: '#FF6B6B' },
      { name: 'Module', color: colors.module }
    ];

    return (
      <div className="flex flex-wrap justify-center gap-4 pt-4">
        {legendItems.map((item) => (
          <div key={item.name} className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-sm text-gray-700">{item.name}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderScenarioBuilder = (scenario) => {
    const isComplete = scenario.polysilicon && scenario.ingotWafer && scenario.cell && scenario.module;
    const hasData = scenario.comboId && case5Data[scenario.comboId];

    return (
      <div
        key={scenario.id}
        className="p-4 rounded-lg border-2 transition-all"
        style={{
          borderColor: isComplete ? '#E78E42' : '#ddd',
          backgroundColor: isComplete ? 'rgba(231, 142, 66, 0.05)' : 'white'
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <input
            type="text"
            value={scenario.name}
            onChange={(e) => updateScenarioName(scenario.id, e.target.value)}
            className="font-semibold text-lg px-2 py-1 border-b-2 focus:outline-none transition-colors"
            style={{ borderColor: '#E78E42', color: '#0A3C80', backgroundColor: 'transparent' }}
          />
          {scenarios.length > 1 && (
            <button
              onClick={() => removeScenario(scenario.id)}
              className="text-red-500 hover:text-red-700 transition-colors"
              title="Remove scenario"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <label className="text-xs font-semibold text-gray-600">Polysilicon</label>
            <select
              value={scenario.polysilicon}
              onChange={(e) => updateScenario(scenario.id, 'polysilicon', e.target.value)}
              className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1"
              style={{ borderColor: scenario.polysilicon ? '#E78E42' : '#ddd' }}
            >
              <option value="">Select...</option>
              {availableCities.map(city => (
                <option key={city} value={city}>{cityToRegion[city] || city}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">Ingot/Wafer</label>
            <select
              value={scenario.ingotWafer}
              onChange={(e) => updateScenario(scenario.id, 'ingotWafer', e.target.value)}
              className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1"
              style={{ borderColor: scenario.ingotWafer ? '#E78E42' : '#ddd' }}
            >
              <option value="">Select...</option>
              {availableCities.map(city => (
                <option key={city} value={city}>{cityToRegion[city] || city}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">Cell</label>
            <select
              value={scenario.cell}
              onChange={(e) => updateScenario(scenario.id, 'cell', e.target.value)}
              className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1"
              style={{ borderColor: scenario.cell ? '#E78E42' : '#ddd' }}
            >
              <option value="">Select...</option>
              {availableCities.map(city => (
                <option key={city} value={city}>{cityToRegion[city] || city}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">Module</label>
            <select
              value={scenario.module}
              onChange={(e) => updateScenario(scenario.id, 'module', e.target.value)}
              className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1"
              style={{ borderColor: scenario.module ? '#E78E42' : '#ddd' }}
            >
              <option value="">Select...</option>
              {availableCities.map(city => (
                <option key={city} value={city}>{cityToRegion[city] || city}</option>
              ))}
            </select>
          </div>
        </div>

        {isComplete && hasData && (
          <div className="mt-3 pt-3 border-t text-center">
            <div className="text-2xl font-bold" style={{ color: '#0A3C80' }}>
              {(() => {
                const data = case5Data[scenario.comboId];
                const value = convertValue(data.statistics.final_msp.mean);
                const currency = unit === 'eurw' ? '€' : '$';
                const decimals = unit === 'm2' ? 2 : 3;
                return `${currency}${value.toFixed(decimals)}`;
              })()}
            </div>
            <div className="text-xs text-gray-600">
              Module MSP (Mean, {getUnitLabel()})
            </div>
          </div>
        )}

        {isComplete && !hasData && (
          <div className="mt-3 pt-3 border-t text-center text-sm text-red-600">
            No data available for this combination
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold" style={{ color: '#000755' }}>
            Scenario Comparison
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Compare cost breakdowns across different supply chain configurations
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Unit selector */}
          <div className="inline-flex rounded-lg border-2 p-1" style={{ borderColor: '#EAC144' }}>
            <button
              onClick={() => setUnit('m2')}
              className={`px-3 py-1 rounded-md text-sm font-semibold transition-all ${
                unit === 'm2' ? 'text-white shadow-md' : 'text-gray-600'
              }`}
              style={unit === 'm2' ? { backgroundImage: 'linear-gradient(to right, #EAC144, #E78E42)' } : {}}
            >
              $/m²
            </button>
            <button
              onClick={() => setUnit('w')}
              className={`px-3 py-1 rounded-md text-sm font-semibold transition-all ${
                unit === 'w' ? 'text-white shadow-md' : 'text-gray-600'
              }`}
              style={unit === 'w' ? { backgroundImage: 'linear-gradient(to right, #EAC144, #E78E42)' } : {}}
            >
              $/W
            </button>
            <button
              onClick={() => setUnit('eurw')}
              className={`px-3 py-1 rounded-md text-sm font-semibold transition-all ${
                unit === 'eurw' ? 'text-white shadow-md' : 'text-gray-600'
              }`}
              style={unit === 'eurw' ? { backgroundImage: 'linear-gradient(to right, #EAC144, #E78E42)' } : {}}
            >
              €/W
            </button>
          </div>

          <button
            onClick={addScenario}
            disabled={scenarios.length >= 6}
            className="px-4 py-2 rounded-lg font-semibold text-sm shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundImage: scenarios.length < 6 ? 'linear-gradient(to right, #E78E42, #EAC144)' : 'none',
              backgroundColor: scenarios.length >= 6 ? '#ccc' : 'transparent',
              color: 'white'
            }}
          >
            + Add Scenario
          </button>
        </div>
      </div>

      {/* Scenario builders */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {scenarios.map(scenario => renderScenarioBuilder(scenario))}
      </div>

      {/* Charts and comparison */}
      {mspChartData.length > 0 ? (
        <>
          {/* MSP Stage Comparison Chart */}
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <h4 className="text-lg font-bold mb-2" style={{ color: '#0A3C80' }}>
              Stage-by-Stage MSP Comparison
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Incremental contribution of each manufacturing stage to total Module MSP
            </p>

            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={mspChartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="scenario"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  label={{
                    value: `MSP (${getUnitLabel()})`,
                    angle: -90,
                    position: 'insideLeft'
                  }}
                />
                <Tooltip content={<CustomMSPTooltip />} />

                <Bar dataKey="Polysilicon" stackId="a" fill={colors.polysilicon} />
                <Bar dataKey="Ingot/Wafer" stackId="a" fill={colors.ingotWafer} />
                <Bar dataKey="Cell" stackId="a" fill={colors.cell} />
                <Bar dataKey="Packing Adj" stackId="a" fill="#FF6B6B" />
                <Bar dataKey="Module" stackId="a" fill={colors.module} />
              </BarChart>
            </ResponsiveContainer>

            <CustomMSPLegend />
          </div>

          {/* Detailed comparison table */}
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <h4 className="text-lg font-bold mb-4" style={{ color: '#0A3C80' }}>
              Detailed MSP Breakdown
            </h4>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead style={{ backgroundImage: 'linear-gradient(to right, rgba(231, 142, 66, 0.2), rgba(234, 193, 68, 0.2))' }}>
                  <tr>
                    <th className="px-4 py-3 text-left font-bold" style={{ color: '#000755' }}>Scenario</th>
                    <th className="px-4 py-3 text-left font-bold text-xs" style={{ color: '#000755' }}>Supply Chain</th>
                    <th className="px-4 py-3 text-right font-bold" style={{ color: colors.polysilicon }}>Polysilicon</th>
                    <th className="px-4 py-3 text-right font-bold" style={{ color: colors.ingotWafer }}>Ingot/Wafer</th>
                    <th className="px-4 py-3 text-right font-bold" style={{ color: colors.cell }}>Cell</th>
                    <th className="px-4 py-3 text-right font-bold" style={{ color: colors.module }}>Module</th>
                    <th className="px-4 py-3 text-right font-bold" style={{ color: '#0A3C80', backgroundColor: 'rgba(10, 60, 128, 0.1)' }}>
                      Total MSP
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {mspChartData.map((row, idx) => {
                    const currency = unit === 'eurw' ? '€' : '$';
                    const decimals = unit === 'm2' ? 2 : 3;

                    const scenario = scenarios.find(s => s.id === row.scenarioId);

                    return (
                      <tr key={row.scenarioId} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 font-semibold text-gray-700">{row.scenario}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {cityToRegion[scenario.polysilicon] || scenario.polysilicon} → {cityToRegion[scenario.ingotWafer] || scenario.ingotWafer} → {cityToRegion[scenario.cell] || scenario.cell} → {cityToRegion[scenario.module] || scenario.module}
                        </td>
                        <td className="px-4 py-3 text-right">{currency}{row.Polysilicon.toFixed(decimals)}</td>
                        <td className="px-4 py-3 text-right">{currency}{row['Ingot/Wafer'].toFixed(decimals)}</td>
                        <td className="px-4 py-3 text-right">{currency}{row.Cell.toFixed(decimals)}</td>
                        <td className="px-4 py-3 text-right">{currency}{row.Module.toFixed(decimals)}</td>
                        <td className="px-4 py-3 text-right font-bold" style={{ color: '#0A3C80', backgroundColor: 'rgba(10, 60, 128, 0.1)' }}>
                          {currency}{row.total.toFixed(decimals)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Note about data */}
          <div className="p-4 rounded-lg border-2" style={{ backgroundImage: 'linear-gradient(to right, rgba(10, 60, 128, 0.05), rgba(231, 142, 66, 0.05))', borderColor: '#0A3C80' }}>
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#0A3C80' }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-gray-700">
                <strong className="font-semibold" style={{ color: '#000755' }}>Note:</strong> Values shown are mean MSP based on 1,000 Monte Carlo iterations per scenario.
                "Packing Adj" represents the -6% adjustment due to cells covering only 94% of module area.
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white p-12 rounded-xl border border-gray-200 text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <p className="text-xl text-gray-600">Build at least one complete scenario to see the comparison</p>
          <p className="text-sm text-gray-500 mt-2">Select locations for all manufacturing stages above</p>
        </div>
      )}
    </div>
  );
};

export default Case5ScenarioComparison;
